import { autoinject } from "aurelia-dependency-injection";
import { observable, computed, action } from "mobx";
import { IInstallationResponse, IInstallationRequest, IDeviceStatusRequest, IDeviceStatusResponse, ICheckOnlineRequest, ICheckResponse, IDeviceConfiguration, ICheckOfflineRequest, IBadgesPackage } from "./DataModel";
import { OnlineService } from "./OnlineService";

const commonHeaders = {
    "Content-Type": "application/json",
    "Accept": "application/json"
};

/**
 * Timeout of a single API request. An endpoint which does not answer in time is
 * treated as unreachable and the next url of the instance is used instead.
 */
export const defaultRequestTimeout = 15000;

/** Calls with an unbounded payload (offline badges, photos, pending checks). */
export const bulkRequestTimeout = 240000;

/**
 * Deployment variables are injected into index.html as #{name} placeholders.
 * A placeholder which has not been replaced means "not configured".
 */
const placeholderPattern = /^#\{.*\}$/;

export function readDeploymentString(value: any): string {
    if (typeof value !== "string") {
        return "";
    }
    const text = value.trim();
    return placeholderPattern.test(text) ? "" : text;
}

export function normalizeApiUrl(url: string): string {
    const text = (url || "").trim();
    if (text === "") {
        return "";
    }
    return text.endsWith("/") ? text : `${text}/`;
}

/**
 * One selectable target instance. All urls of an instance point to the same
 * backend through different domains, so they are fully interchangeable.
 */
export interface ApiInstance {
    name: string;
    urls: string[];
}

/** Shape of a single item of the static `window.apiMap` deployment variable. */
export interface ApiMapItem {
    name: string;
    url: string;
}

/**
 * Reads instances either from the static apiMap ({ name, url }) or from the
 * urls discovery endpoint ({ name, urls: [] }). Invalid items are dropped so a
 * broken response can never wipe a working configuration.
 */
export function parseApiInstances(data: any): ApiInstance[] {
    if (!Array.isArray(data)) {
        return [];
    }

    const instances: ApiInstance[] = [];
    data.forEach(item => {
        if (!item || typeof item.name !== "string" || item.name.trim() === "") {
            return;
        }
        const name = item.name.trim();
        const rawUrls = Array.isArray(item.urls)
            ? item.urls
            : (typeof item.url === "string" ? [item.url] : []);
        const urls: string[] = rawUrls
            .map((i: any) => typeof i === "string" ? normalizeApiUrl(i) : "")
            .filter((i: string) => i !== "")
            .filter((i: string, index: number, all: string[]) => all.indexOf(i) === index);

        if (urls.length === 0 || instances.some(i => i.name === name)) {
            return;
        }
        instances.push({ name: name, urls: urls });
    });

    return instances;
}

/** Endpoints of the persisted settings, with fallback to the legacy single url. */
export function resolveApiRoots(apiRoots: string[], apiRoot: string): string[] {
    if (apiRoots && apiRoots.length > 0) {
        return apiRoots.slice();
    }
    return apiRoot ? [apiRoot] : [];
}

/** True for the AbortError raised by our own request timeout. */
function isTimeout(error: any): boolean {
    return !!error && (error.name === "AbortError" || error.name === "TimeoutError");
}

export async function fetchWithTimeout(url: string, init: RequestInit, timeout: number): Promise<Response> {
    if (!timeout || typeof AbortController === "undefined") {
        return fetch(url, init);
    }

    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), timeout);
    try {
        return await fetch(url, { ...init, signal: controller.signal });
    } finally {
        clearTimeout(timeoutHandle);
    }
}

@autoinject()
export class ApiService {
    constructor(private onlineService: OnlineService) {
        if (this.isDiscoveryEnabled) {
            console.log(`[ApiService] urls discovery enabled: ${this.discoveryUrl}`);
        } else {
            console.table(this.staticApiInstances);
        }
    }

    defaultApiRoot = readDeploymentString((<any>window).defaultApiRoot); // "http://localhost:5000/api-external/v1/";
    discoveryUrl = readDeploymentString((<any>window).apiDiscoveryUrl);
    staticApiInstances: ApiInstance[] = parseApiInstances((<any>window).apiMap);

    /** Urls of the selected instance, always tried in this order. */
    @observable apiRoots: string[] = this.defaultApiRoot ? [this.defaultApiRoot] : [];
    @observable apiInstanceName: string = null;
    @observable deviceId: string = null;
    @observable apiSecret: string = null;
    onUnauthorize: () => Promise<any>;

    @computed get isInitialized(): boolean {
        return !!this.apiSecret && this.apiSecret !== ""
            && !!this.deviceId && this.deviceId !== "";
    }

    /** Primary url - kept for display and for the legacy single-url settings. */
    @computed get apiRoot(): string {
        return this.apiRoots.length > 0 ? this.apiRoots[0] : null;
    }

    get isDiscoveryEnabled(): boolean {
        return this.discoveryUrl !== "";
    }

    /** True when the target is picked from a list instead of typed by the user. */
    @computed get hasInstanceSelection(): boolean {
        return this.isDiscoveryEnabled || this.staticApiInstances.length > 0;
    }

    @computed get canSend(): boolean {
        return this.isInitialized && this.onlineService.isOnline;
    }

    @action setEndpoints(instanceName: string, apiRoots: string[]) {
        this.apiInstanceName = instanceName || null;
        this.apiRoots = (apiRoots || []).map(normalizeApiUrl).filter(i => i !== "");
    }

    async install(installationToken: string, newInstallationToken: string, deviceCode: string, deviceName: string): Promise<IInstallationResponse> {
        const request: IInstallationRequest = {
            CurrentInstallationToken: installationToken,
            InstallationToken: newInstallationToken,
            DeviceCode: deviceCode,
            DeviceName: deviceName
        };

        return this.post<IInstallationResponse>(`device/install/${encodeURIComponent(newInstallationToken)}`, request);
    }

    async updateStatus(request: IDeviceStatusRequest): Promise<IDeviceStatusResponse> {
        return this.post<IDeviceStatusResponse>(`device/${encodeURIComponent(this.deviceId)}/status`, request);
    }

    async getDeviceConfiguration(): Promise<IDeviceConfiguration> {
        return this.get(`device/${this.deviceId}/configuration`, null, bulkRequestTimeout);
    }

    async getOfflineBadgesPackage(gateId: string, fromVersion: number): Promise<IBadgesPackage> {
        const data = await this.get<IBadgesPackage>(`badge`, {
            gateId: gateId,
            fromVersion: fromVersion
        }, bulkRequestTimeout);

        return data;
    }
    async getBadgePhoto(photoId: string): Promise<Blob> {
        return this.getBlob(`badge/photo/${photoId}`, null, bulkRequestTimeout);
    }

    async check(badge: ICheckOnlineRequest): Promise<ICheckResponse> {
        return this.post<ICheckResponse>(`gate/${badge.GateId}/check`, badge, { gateId: badge.GateId });
    }
    async checkSync(badges: ICheckOfflineRequest[]): Promise<boolean> {
        const gateIds = badges.map(i => i.GateId).filter((v, i, a) => a.indexOf(v) === i);
        if (gateIds.length !== 1) {
            throw new Error(`Gate id must be same for all badges. Ids ${gateIds}`);
        }
        return this.postNoReturn(`gate/${gateIds[0]}/tick-results`, badges, { gateId: gateIds[0], deviceId: this.deviceId }, bulkRequestTimeout);
    }

    async logError(timestamp: string, error: any) {
        if (!this.deviceId) {
            return Promise.resolve({});
        }

        const log = {
            Timestamp: timestamp,
            ErrorType: error.name,
            ErrorMessage: error.message,
            ErrorStack: error.stack,
        };
        return this.post<string>(`device/${encodeURIComponent(this.deviceId)}/log/error`, log);
    }

    async get<T>(url: string, queryParams: { [id: string]: string | number | boolean } = null, timeout: number = defaultRequestTimeout): Promise<T> {
        const response = await this.action("GET", url, queryParams, null, timeout);
        if (!response.ok) {
            throw new Error("response invalid");
        }

        return response.json();
    }
    async getBlob(url: string, queryParams: { [id: string]: string | number | boolean } = null, timeout: number = defaultRequestTimeout): Promise<Blob> {
        const response = await this.action("GET", url, queryParams, null, timeout);
        if (!response.ok) {
            throw new Error("response invalid");
        }
        return response.blob();
    }


    async postNoReturn(url: string, body: any, queryParams: { [id: string]: string | number | boolean } = null, timeout: number = defaultRequestTimeout): Promise<boolean> {
        const r = await this.action("POST", url, queryParams, body, timeout);
        return r.ok && !r.redirected;
    }

    async post<T>(url: string, body: any, queryParams: { [id: string]: string | number | boolean } = null): Promise<T> {
        const r = await this.action("POST", url, queryParams, body);
        return r.json();
    }

    async action(action: string, url: string, queryParams: { [id: string]: string | number | boolean }, body: any = null, timeout: number = defaultRequestTimeout): Promise<Response> {
        const headers = { "X-ApiKey": this.apiSecret, ...commonHeaders };
        const queryString = queryParams
            ? Object.keys(queryParams)
                .filter(i => queryParams[i] !== undefined)
                .reduce((a, v, i) => `${a}${i === 0 ? "?" : "&"}${encodeURIComponent(v)}=${encodeURIComponent(queryParams[v])}`, "")
            : "";
        const roots = this.apiRoots.slice();
        if (roots.length === 0) {
            throw new Error("API endpoint is not configured");
        }

        const init: RequestInit = {
            method: action,
            headers: headers,
            body: body !== null ? JSON.stringify(body) : null,
        };

        // Every call starts again with the first url. A url is skipped only when
        // it cannot be reached at all (dns/connection failure or timeout) - any
        // http answer means the endpoint works and belongs to the caller.
        let response: Response = null;
        let lastError: any = null;
        for (let i = 0; i < roots.length && response === null; i++) {
            try {
                response = await fetchWithTimeout(`${roots[i]}${url}${queryString}`, init, timeout);
                if (i > 0) {
                    console.warn(`[ApiService] fallback endpoint used: ${roots[i]}`);
                }
            } catch (e) {
                lastError = e;
                console.warn(`[ApiService] endpoint not reachable: ${roots[i]} (${e && e.message})`);
                if (isTimeout(e) && action !== "GET") {
                    // the request may have reached the backend already and both
                    // urls lead to the same one - repeating it could apply the
                    // very same check/result twice
                    break;
                }
            }
        }

        if (response === null) {
            throw lastError || new Error("no API endpoint is reachable");
        }

        if (response.status === 401 && this.onUnauthorize) {
            await this.onUnauthorize();
            throw new Error("Unauthorize");
        }
        return response;
    }

}
